import { useRef, ReactNode } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FloatingParticles } from './FloatingParticles';
import { useReducedMotion } from '@/hooks/use-reduced-motion';

interface ParallaxHeroProps {
  children: ReactNode;
  className?: string;
  showParticles?: boolean;
  particleCount?: number;
}

export function ParallaxHero({ 
  children, 
  className = '',
  showParticles = true,
  particleCount = 20
}: ParallaxHeroProps) {
  const prefersReducedMotion = useReducedMotion();
  const heroRef = useRef<HTMLElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  // Only apply parallax transforms if motion is not reduced
  const gradientY = useTransform(scrollYProgress, [0, 1], prefersReducedMotion ? ["0%", "0%"] : ["0%", "50%"]);
  const gradientOpacity = useTransform(scrollYProgress, [0, 0.8], prefersReducedMotion ? [1, 1] : [1, 0.3]);
  const gradientScale = useTransform(scrollYProgress, [0, 1], prefersReducedMotion ? [1, 1] : [1, 1.2]);

  // Simplified render for reduced motion
  if (prefersReducedMotion) {
    return (
      <section ref={heroRef} className={`relative overflow-hidden ${className}`} style={{ contain: 'layout style' }}>
        {/* Static Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-primary/3 to-accent/15" />
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-accent/5 to-primary/10 opacity-80" />
        
        {/* Static Particles */}
        {showParticles && <FloatingParticles count={Math.floor(particleCount / 2)} />}
        
        {/* Content */}
        <div className="relative z-10">
          {children}
        </div>
      </section>
    );
  }

  return (
    <section 
      ref={heroRef} 
      className={`relative overflow-hidden ${className}`}
      style={{ contain: 'layout style paint', willChange: 'transform' }}
    >
      {/* Primary Parallax Background Gradient */}
      <motion.div 
        className="absolute inset-0 bg-gradient-to-br from-primary/8 via-primary/3 to-accent/15"
        style={{ 
          y: gradientY,
          opacity: gradientOpacity,
          scale: gradientScale,
        }}
      />
      
      {/* Secondary Gradient Layer */}
      <motion.div 
        className="absolute inset-0 bg-gradient-to-tr from-transparent via-accent/5 to-primary/10"
        style={{ 
          y: useTransform(scrollYProgress, [0, 1], ["0%", "30%"]),
          opacity: useTransform(scrollYProgress, [0, 1], [0.8, 0.2]),
        }}
      />
      
      {/* Floating Particles */}
      {showParticles && <FloatingParticles count={particleCount} />}
      
      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </section>
  );
}

export default ParallaxHero;
